import { Button, DialogActions, DialogContent, Typography } from "@mui/material";
import React, { Fragment, ReactNode, useCallback, useMemo } from "react";
import { sumBy } from "lodash";
import { SquaredChip } from "../../../../common/component/squared-chip/squared-chip";
import { blue } from "@mui/material/colors";
import { t } from "i18next";
import { PalletsListItem } from "../pallets-list/pallets-list-item";
import { PalletShippingInformationInput } from "./pallet-shipping-information/pallet-shipping-information-input";
import { usePalletShippingContext } from "./pallet-shipping-context";
import { PalletShipHandler } from "../../../../common/cqrs/command/pallet-ship/pallet-ship-handler";
import { PalletPickupSlotShippingCommand } from "../../../../common/cqrs/command/pallet-ship/pallet-pickup-slot-shipping-command";
import { PalletManualShippingCommand } from "../../../../common/cqrs/command/pallet-ship/pallet-manual-shipping-command";
import { notificationModel } from "../../../../common/component/notifications/notification-model";

import "./style.scss";

interface IPalletShippingFormBaseProps {
    title: ReactNode;
    palletsList: PalletsListItem[];
    isTruckIdentifierRequired: boolean;
    onStepCompleted: (palletList: PalletsListItem[]) => void;
    onCancel: () => void;
}

interface IPalletShippingFormProps extends IPalletShippingFormBaseProps {
    hasNextShippingStep: boolean;
}

export const PalletShippingForm = (props: IPalletShippingFormProps) => {
    const {
        isFormValid,
        comment,
        shippingInformation: { truckIdentifier, containerSealNumber, pickingDate, id: palletPickupSlotId, pickupSlotName },
    } = usePalletShippingContext();
    const isMultiCarrier = useMemo(() =>
            props.palletsList.find(p => p.carrier !== props.palletsList[0].carrier) !== undefined,
        [props.palletsList]);

    const handleFormSubmission = useCallback(async () => {
        if (palletPickupSlotId) {
            await PalletShipHandler(new PalletPickupSlotShippingCommand(props.palletsList.map(p => p.id),
                palletPickupSlotId, containerSealNumber, comment));

            notificationModel.addSuccessMessage(t("components.pallet.shipping.successSlot", {
                count: props.palletsList.length,
                carrier: props.palletsList[0].carrier,
                pickupSlot: pickupSlotName,
            }));
        } else {
            await PalletShipHandler(new PalletManualShippingCommand(props.palletsList.map(p => p.id),
                truckIdentifier, pickingDate, containerSealNumber, comment));

            notificationModel.addSuccessMessage(props.palletsList.length === 1 ?
                t("components.pallet.shipping.success", { palletCode: props.palletsList[0].code }) :
                t("components.pallet.shipping.successMultiple", { count: props.palletsList.length }));
        }

        props.onStepCompleted(props.palletsList);
    }, [props.palletsList, palletPickupSlotId, truckIdentifier, pickingDate, containerSealNumber, comment]);

    const handleFormSkipped = useCallback(() => {
        props.onStepCompleted(props.palletsList);
    }, [props.onStepCompleted]);

    return <Fragment>
        {props.title}
        <DialogContent className={"pallet-shipping-dialog__content pallet-shipping-dialog__content--no-bottom-padding"}>
            <div className={"pallet-shipping-summary"}>
                <Typography>
                    {t("components.pallet.shipping.description", {
                        palletCount: isMultiCarrier ?
                            `${props.palletsList.length}` :
                            `${props.palletsList.length} ${props.palletsList[0].carrier}`,
                    })}
                </Typography>
                <div className={"pallet-shipping-summary__list"}>
                    {props.palletsList.map(c => <SquaredChip key={`item-${c.code}`} propertyKey={`item-${c.code}`}
                                                             color={blue} label={c.code} />)}
                </div>
                <Typography className={"pallet-shipping-details__summary"}>
                    {t("components.pallet.shipping.descriptionParcelCount", {
                        count: sumBy(props.palletsList, "parcelCount"),
                    })}
                </Typography>
            </div>
        </DialogContent>
        <DialogContent className={"pallet-shipping-dialog__content"}>
            <div className={"pallet-shipping-form"}>
                <Typography className={"pallet-shipping-form__headline"}>
                    {t("components.pallet.shipping.informationTitle")}
                </Typography>
                <PalletShippingInformationInput key={`pallet-shipping-data-selector-${props.palletsList[0].carrier}`}
                                                palletsList={props.palletsList}
                                                isTruckIdentifierRequired={props.isTruckIdentifierRequired} />
            </div>
        </DialogContent>
        <DialogActions>
            <Button color={"inherit"}
                    variant={"contained"}
                    type={"submit"}
                    data-testid={"pallet-shipping-cancel"}
                    onClick={props.onCancel}>
                {t("components.popin.cancel")}
            </Button>
            {props.hasNextShippingStep &&
                <Button color={"inherit"}
                        variant={"contained"}
                        type={"submit"}
                        data-testid={"pallet-shipping-skip"}
                        onClick={handleFormSkipped}>
                    {t("components.pallet.shipping.skipShipping")}
                </Button>}
            <Button color={"secondary"}
                    variant={"contained"}
                    type={"submit"}
                    data-testid={"pallet-shipping-submit"}
                    key={"submit-btn"}
                    disabled={!isFormValid}
                    onClick={handleFormSubmission}>
                {t("components.pallet.shipping.ship")}
            </Button>
        </DialogActions>
    </Fragment>;
};
